/**
 * User IP Address Utility
 *
 * A composable for fetching, storing, and retrieving user IP addresses
 * Uses ipify API to fetch the IP and stores it in cookies for future use
 */

import { ref } from 'vue';

import { getCookie, setCookie } from '~/utils/cookies.js';

// Constants
export const IP_API_URL = 'https://api.ipify.org?format=json';
const IP_COOKIE_NAME = 'user_ip';
const IP_COOKIE_EXPIRY_DAYS = 7;

/**
 * User IP address composable
 * Provides utilities for fetching and storing user IP addresses
 *
 * @returns {import('~/types/user-ip').UserIpReturn} IP utility methods and state
 */
export function useUserIpApi() {
  const userIp = ref(null);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetches the user's IP address from the API
   * @returns {Promise<string|null>} Promise resolving to the user's IP address
   */
  const fetchIpFromApi = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await fetch(IP_API_URL);

      if (!response.ok) {
        throw new Error(`Failed to fetch IP: ${response.status}`);
      }

      const data = await response.json();

      if (!data.ip) {
        throw new Error('IP not found in response');
      }

      // Store the IP in a cookie for future use
      setCookie(IP_COOKIE_NAME, data.ip, { days: IP_COOKIE_EXPIRY_DAYS });

      userIp.value = data.ip;
      return data.ip;
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Gets the user's IP address from cookies or fetches it if not available
   * @param {boolean} forceFetch - Whether to force fetch from API even if cookie exists
   * @returns {Promise<string|null>} Promise resolving to the user's IP address
   */
  const getUserIp = async (forceFetch = false) => {
    if (userIp.value && !forceFetch) {
      return userIp.value;
    }

    const cookieIp = getCookie(IP_COOKIE_NAME);

    if (cookieIp && !forceFetch) {
      userIp.value = cookieIp;
      return cookieIp;
    }

    return await fetchIpFromApi();
  };

  return {
    error,
    fetchIpFromApi,
    getUserIp,
    isLoading,
    userIp,
  };
}
